import React from "react";
import { motion } from "framer-motion";
import {
  ClipboardList,
  UserPlus,
  FileCheck2,
  Clock,
  Search,
  Trophy,
  FileText,
  AppWindow,
  ChevronDown,
} from "lucide-react";

const steps = [
  { icon: ClipboardList, label: "Buyer Creates Bidding Request" },
  { icon: UserPlus, label: "Verified Manufacturers Invited" },
  { icon: FileCheck2, label: "Manufacturers Submit Factory Offers" },
  { icon: Clock, label: "Bidding Closes" },
  { icon: Search, label: "Buyer Reviews Offers" },
  { icon: Trophy, label: "Winning Manufacturer Awarded" },
  { icon: FileText, label: "Purchase Order" },
  { icon: AppWindow, label: "Continue Inside Workspace" },
];

export default function HowItWorks() {
  return (
    <section
      data-testid="how-it-works-section"
      id="how-it-works"
      className="relative py-24 md:py-32 bg-[#F8FAFC]"
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="font-inter text-[11px] font-bold tracking-[0.22em] uppercase text-[#3B82F6]">
            How CommodityBid Works
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-[42px] font-extrabold tracking-tight text-[#0B1F3A] leading-[1.1]">
            One structured process.<br className="hidden md:block" />
            <span className="text-[#475569]">From bidding request to award.</span>
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#475569] leading-relaxed max-w-xl">
            You launch one bidding request. DeMaxtore organizes the online bidding process.
            Verified manufacturers submit competitive factory offers. You award the winning manufacturer — all inside one Workspace.
          </p>
        </motion.div>

        <div className="mt-14 relative max-w-2xl mx-auto">
          <div className="absolute left-[27px] md:left-[31px] top-8 bottom-8 w-px bg-gradient-to-b from-[#3B82F6]/40 via-[#3B82F6]/20 to-[#3B82F6]/40 hidden sm:block" />

          <div className="space-y-3">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isLast = i === steps.length - 1;
              return (
                <motion.div
                  key={step.label}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  data-testid={`how-it-works-step-${i}`}
                  className="relative"
                >
                  <div
                    className={`flex items-center gap-4 md:gap-5 rounded-[20px] border p-4 md:p-5 transition-all duration-300 ${
                      isLast
                        ? "bg-[#0B1F3A] border-[#0B1F3A] text-white cb-elevated-shadow"
                        : "bg-white border-slate-200/80 hover:border-[#3B82F6]/30 hover:shadow-[0_8px_30px_-8px_rgba(15,23,42,0.08)]"
                    }`}
                  >
                    <div
                      className={`relative z-10 h-14 w-14 shrink-0 rounded-xl flex items-center justify-center ${
                        isLast ? "bg-[#3B82F6]" : "bg-[#3B82F6]/10 text-[#3B82F6]"
                      }`}
                    >
                      <Icon className={`h-6 w-6 ${isLast ? "text-white" : ""}`} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-inter text-[10px] font-bold tracking-[0.2em] uppercase text-[#64748B] mb-0.5">
                        Step {String(i + 1).padStart(2, "0")}
                      </p>
                      <p className={`font-display text-[15px] md:text-[17px] font-bold tracking-tight ${
                        isLast ? "text-white" : "text-[#0B1F3A]"
                      }`}>
                        {step.label}
                      </p>
                    </div>
                    <span className={`hidden md:inline text-[11px] font-bold tabular-nums ${
                      isLast ? "text-white/40" : "text-slate-300"
                    }`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  {!isLast && (
                    <div className="flex justify-center py-1 sm:hidden">
                      <ChevronDown className="h-4 w-4 text-[#3B82F6]/50" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
